import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';
import { UserOut } from '../types/api';

interface User extends UserOut {}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  loginWithGoogle: (token: string) => Promise<void>;
  loginWithMicrosoft: (token: string) => Promise<void>;
  loginWithApple: (idToken: string, userData?: any) => Promise<void>;
  logout: () => void;
  register: (userData: any) => Promise<any>;
  updateUser: (userData: User) => void;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (apiService.isAuthenticated()) {
          const userData = await apiService.getMyProfile();
          setUser(userData);
        }
      } catch (error) {
        // If getting user profile fails, clear auth
        apiService.removeAuthToken();
        localStorage.removeItem('user');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await apiService.login({ username_or_email: username, password });
      apiService.setAuthToken(response.access_token);
      
      // Get user profile
      const userData = await apiService.getMyProfile();
      setUser(userData);
      
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async (token: string) => {
    try {
      const response = await apiService.loginWithGoogle(token);
      apiService.setAuthToken(response.access_token);
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const loginWithMicrosoft = async (token: string) => {
    try {
      const response = await apiService.loginWithMicrosoft(token);
      apiService.setAuthToken(response.access_token);
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const loginWithApple = async (idToken: string, userData?: any) => {
    try {
      const response = await apiService.loginWithApple(idToken, userData);
      apiService.setAuthToken(response.access_token);
      setUser(response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    apiService.removeAuthToken();
    localStorage.removeItem('user');
    setUser(null);
  };

  const register = async (userData: any) => {
    try {
      const response = await apiService.register(userData);
      // After registration, you might want to auto-login or redirect to login
      return response;
    } catch (error) {
      throw error;
    }
  };

  const updateUser = (userData: User) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const refreshUser = async () => {
    try {
      if (apiService.isAuthenticated()) {
        const userData = await apiService.getMyProfile();
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    loginWithApple,
    logout,
    register,
    updateUser,
    refreshUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 